// Which open grid shifts a team member could pick up — shared by the Schedule Review page
// (to ring them in the grid) and unit-testable in isolation. Fed by mock data this phase;
// swap the inputs for GraphQL once worker eligibility and shift data land.
import type {
  ScheduleDayType,
  ScheduleShiftType,
  ScheduleTeamMemberType,
} from '@load-balancing/types';
// Sibling import (not the @load-balancing/services barrel, which re-exports both files) so the
// "Eligible shifts" ring and the drag drop ring share ONE eligibility rule and never diverge.
import {
  computeOpenSlotDropValidity,
  heldDaySectionsFor,
} from './shift-eligibility';

/**
 * Open shifts the member could fill — the SAME eligibility the drag drop ring enforces
 * ({@link computeOpenSlotDropValidity}): an unfilled, non-time-off slot the member is active for on
 * that date, whose role (per the covering term) matches (or a charge slot they're charge-eligible
 * for), and that doesn't clash with a shift they already hold that week. Returns the matching shift
 * ids across every week (in week then in-week order). With `weekendOnly`, restricts to
 * Saturday/Sunday columns.
 */
export function getAvailableShiftIdsForMember(
  member: ScheduleTeamMemberType,
  shiftsByWeek: Record<string, ScheduleShiftType[]>,
  daysByWeek: Record<string, ScheduleDayType[]>,
  // eligibleRolePairs: the run's cross-role EligibilityRule matrix (WORK-6513), forwarded to
  // computeOpenSlotDropValidity so the "Eligible shifts" ring honors it exactly like the drag ring.
  options: {
    weekendOnly?: boolean;
    eligibleRolePairs?: ReadonlySet<string>;
  } = {}
): string[] {
  const { weekendOnly = false, eligibleRolePairs = new Set<string>() } =
    options;

  return Object.entries(shiftsByWeek).flatMap(([weekKey, weekShifts]) => {
    const days = daysByWeek[weekKey] ?? [];
    const weekendDayKeys = new Set(
      days.filter((day) => day.weekend).map((day) => day.key)
    );
    // dayKey → calendar date, so each open slot resolves to the date driving its per-slot
    // eligibility (active / role / charge) via the covering term.
    const dateByDayKey = new Map(days.map((day) => [day.key, day.date]));
    // Day+section slots the member already holds this week — used to skip clashes. Reuses the drag
    // path's helper so both rings resolve held slots the SAME way: by `assignedWorkerId` (uuid),
    // with a name fallback only for rows carrying no id. Matching by name alone mis-attributed slots
    // between two members sharing a name (the class of bug WORK-6573 fixed for the drag path). No
    // drag source to exclude here, so `fromShiftId` is undefined.
    const heldSlots = heldDaySectionsFor(weekShifts, member, undefined);

    return weekShifts
      .filter((shift) =>
        isAvailableForMember(
          shift,
          member,
          heldSlots,
          dateByDayKey.get(shift.dayKey),
          eligibleRolePairs
        )
      )
      .filter((shift) => !weekendOnly || weekendDayKeys.has(shift.dayKey))
      .map((shift) => shift.id);
  });
}

function isAvailableForMember(
  shift: ScheduleShiftType,
  member: ScheduleTeamMemberType,
  heldSlots: Set<string>,
  slotDate?: string,
  eligibleRolePairs: ReadonlySet<string> = new Set()
): boolean {
  // Only OPEN, staffable slots are ringable; the drop-validity rule owns the rest (role per the
  // covering term incl. the cross-role matrix, active-on-the-date, charge-eligibility, and the
  // day+band clash).
  if (shift.workerName) return false;
  if (shift.source === 'pto' || shift.fixedCommitment) return false;
  return computeOpenSlotDropValidity(
    member,
    shift,
    heldSlots,
    slotDate,
    eligibleRolePairs
  ).valid;
}

/**
 * Shifts already assigned to the member across all weeks. Returns the matching shift ids
 * in week then in-week order. Used to fill the grid with an elm background so schedulers
 * can see where the worker's time already sits before deciding where to add more.
 */
export function getAssignedShiftIdsForMember(
  member: ScheduleTeamMemberType,
  shiftsByWeek: Record<string, ScheduleShiftType[]>
): string[] {
  return Object.values(shiftsByWeek)
    .flat()
    .filter((shift) => shift.assignedWorkerId === member.id)
    .map((shift) => shift.id);
}
